<template>
  <div>
    <p
      :id="$options.uniqueId"
      class="visuallyHidden"
      data-test-id="PasswordComplexity__ariaInputDescription"
    >
      Password must
      <template v-for="(row) in helperText">
        {{ row.label }}.
      </template>
    </p>
    <InputPassword
      v-model="newPassword"
      :ariaDescribedBy="$options.uniqueId"
      autocomplete="new-password"
      autofocus
      data-test-id="PasswordComplexity__PasswordInput"
      label="New Password"
      :required="true"
      :rules="rules"
      :showValidationMessage="false"
      viewableToggle
      @blur="handleBlur"
      @focus="handleFocus"
    />
    <ul
      aria-hidden="true"
      class="list"
    >
      <li
        v-for="(row) in helperText"
        :key="row.id"
        :class="['listItem', [row.color]]"
        :data-test-id="`PasswordComplexity__${row.id}`"
      >
        <Icon
          v-if="row.icon !== $options.icons.noIcon"
          :class="['requirementIcon', [row.icon]]"
          :data-test-id="`PasswordComplexity__icon__${row.icon}`"
          :icon="row.icon"
          :scale="1.25"
        />
        <span
          v-else
          :class="['requirementIcon', 'bullet']"
          :data-test-id="`PasswordComplexity__noIcon`"
        />{{ row.label }}
      </li>
    </ul>
  </div>
</template>

<script>
import {
  Icon,
  InputPassword,
  addValidationRule,
} from '@jumpcloud/ui-components';

import { icons } from 'client/systemusers/constants/PasswordComplexityConstants';
import Util from 'client/util/Util';

export const passwordMaxLength = 64;
const uniqueId = Util.getUniqueId();

const validationResults = {
  invalid: false,
  notChecked: undefined,
  valid: true,
};

const colors = {
  invalidColor: 'invalid',
  notCheckedYet: 'notCheckedYet',
  validColor: 'valid',
};

const backendValidationResults = {
  [validationResults.valid]: { icon: icons.validIcon, color: colors.validColor },
  [validationResults.invalid]: { icon: icons.backendValidationIcon, color: colors.invalidColor },
  [validationResults.notChecked]: {
    icon: icons.backendValidationIcon,
    color: colors.notCheckedYet,
  },
};

const clientValidationResults = {
  [validationResults.valid]: { icon: icons.validIcon, color: colors.validColor },
  [validationResults.invalid]: { icon: icons.noIcon, color: colors.invalidColor },
};

export const serverValidationMessages = {
  passwordHistory: (maxHistory) => `Cannot be any of your last ${maxHistory} passwords`,
};

export default {
  name: 'PasswordComplexity',

  components: {
    Icon,
    InputPassword,
  },

  props: {
    passwordHistoryFailed: {
      type: Boolean,
      default: false,
    },

    passwordPolicy: {
      type: Object,
      default: () => ({}),
    },

    useBackendValidations: {
      type: Boolean,
      default: true,
    },

    username: {
      type: String,
      default: '',
    },

    value: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      active: undefined,
      helperText: [],
      isValid: undefined,
    };
  },

  emits: ['input', 'isValidPassword'],

  computed: {
    backendValidations() {
      const { enableMaxHistory, maxHistory } = this.passwordPolicy;

      const validations = [];

      if (enableMaxHistory) {
        validations.push({
          id: 'noHistory',
          label: serverValidationMessages.passwordHistory(maxHistory),
          validate: () => (this.passwordHistoryFailed
            ? validationResults.invalid
            : validationResults.notChecked),
        });
      }

      return validations;
    },

    clientValidations() {
      const { username } = this;
      const {
        allowUsernameSubstring, enableMinLength, minLength, needsLowercase,
        needsNumeric, needsSymbolic, needsUppercase,
      } = this.passwordPolicy;

      return [{
        id: 'noUsername',
        label: 'Not contain your username',
        enabled: !allowUsernameSubstring,
        validate(value) {
          return !value.toLowerCase().includes(username.toLowerCase());
        },
      }, {
        id: 'minLength',
        label: `Have at least ${minLength} characters`,
        validate(value) {
          return value.length >= minLength;
        },
        enabled: enableMinLength,
      }, {
        id: 'maxLength',
        label: `Have less than ${passwordMaxLength} characters`,
        enabled: true,
        validate(value) {
          return value.length < passwordMaxLength;
        },
      }, {
        id: 'lowercase',
        label: 'Have one lowercase character',
        validate(value) {
          return /[a-z]/.test(value);
        },
        enabled: needsLowercase,
      }, {
        id: 'uppercase',
        label: 'Have one capital letter',
        validate(value) {
          return /[A-Z]/.test(value);
        },
        enabled: needsUppercase,
      }, {
        id: 'number',
        label: 'Have one number',
        validate(value) {
          return /[0-9]/.test(value);
        },
        enabled: needsNumeric,
      }, {
        id: 'specialChar',
        label: 'Have one special character (!@#$%^&*_-)',
        validate(value) {
          return /[\W_]/.test(value);
        },
        enabled: needsSymbolic,
      }].filter(req => req.enabled);
    },

    newPassword: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.handleRequirements(newValue);
        this.$emit('input', newValue);
      },
    },
    rules() {
      const rules = ['required'];

      if (!this.isValid) {
        rules.push('localValidation');
      }

      return rules.join('|');
    },
  },

  watch: {
    passwordHistoryFailed() {
      this.handleRequirements(this.newPassword);
    },
  },

  created() {
    addValidationRule('localValidation', {
      validate: () => false,
      message: '',
    });
  },

  mounted() {
    this.handleRequirements('');
  },

  methods: {
    handleBlur() {
      this.active = false;
      this.$emit('blur');
    },

    handleFocus() {
      this.active = true;
      this.$emit('focus');
    },

    handleRequirements(value) {
      const helperTextRows = [];
      let valid = true;

      if (this.useBackendValidations) {
        this.backendValidations.forEach(({
          validate, label, id,
        }) => {
          const validationResult = validate(value);

          helperTextRows.push({
            ...backendValidationResults[validationResult],
            id,
            label,
          });
        });
      }

      this.clientValidations.forEach(({
        validate, label, id,
      }) => {
        const validationResult = validate(value);

        if (!validationResult) {
          valid = false;
        }

        helperTextRows.push({
          ...clientValidationResults[validationResult],
          id,
          label,
        });
      });

      this.helperText = helperTextRows;
      this.isValid = valid;
      this.$emit('isValidPassword', valid);
    },
  },

  icons,
  uniqueId,
};
</script>

<style scoped>
@import '../../css/typography.css';

@value ( jcError900, jcGrey, jcGrey100, jcGrey800, jcSuccess900 ) from '~css/brand-colors.css';

.bullet {
  background-color: var(--jc-color-disabled);
  border-radius: 50%;
  display: inline-block;
  height: 0.961rem;
  width: 0.961rem;
}

.list {
  list-style-type: none;
  padding-left: 0;
}

.listItem {
  align-items: center;
  color: var(--jcGrey);
  display: flex;
  font-size: var(--jcBodyTiny);
  line-height: 1.269rem;
  margin-bottom: 0.4848rem;
}

.listItem.valid {
  color: inherit;
}

.listItem.valid .requirementIcon {
  color: var(--jcSuccess900);
}

.listItem.invalid .requirementIcon {
  color: var(--jcError900);
}

.listItem.notCheckedYet .requirementIcon {
  color: var(--jcGrey800);
}

.requirementIcon.requirementIcon {
  margin-right: 0.606rem;
}
</style>
